import { Injectable, inject } from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { Range } from "../models/range-status-days";

export type FilterType = "array" | "string" | "range" | "number";

/**
 * A `{ start?, end? }` range, as stored in localStorage for range filters — the
 * pages' own `Range` (`models/range-status-days`, 9 importers), re-exported under
 * the name the codec uses so the two cannot drift apart.
 *
 * The import at the top of this file is load-bearing: `Range` is a DOM global too
 * (a text selection), and without it this would silently resolve to that.
 */
export type FilterRange = Range;

/** What every param declares, whatever its type. */
interface FilterParamBase {
  /** URL query-param key, e.g. "pkg". Globally unique across every config. */
  readonly paramKey: string;
  /**
   * localStorage key this filter is cached under. Its presence is what makes the
   * service authoritative for the value: declared → the service reads and writes
   * that key; omitted → the value lives on the page component (or in a signal
   * store), and the service only encodes/decodes it to/from the URL.
   *
   * It is also what decides whether a param can decode to nothing: a stored one
   * always resolves to a real value of its type, an unstored one falls back to
   * `undefined` (see `cachedValue`), which is what `FilterValueOf` reads off.
   */
  readonly storageKey?: string;
}

/** A multiselect: comma-joined in the URL, a JSON array in localStorage. */
export interface ArrayFilterParam extends FilterParamBase {
  readonly type: "array";
}

/** A `start-end` range (either half may be missing: `5-`, `-5`). */
export interface RangeFilterParam extends FilterParamBase {
  readonly type: "range";
}

/** A single number (an index, a day count). */
export interface NumberFilterParam extends FilterParamBase {
  readonly type: "number";
}

/**
 * A single string — plain, or a radio group when it declares a `defaultValue`.
 * `jsonEncoded`, `defaultValue` and `allowedValues` live on this member alone:
 * they were never meaningful for an array, a range or a number, and the union is
 * what stops one being declared there by accident.
 */
export interface StringFilterParam extends FilterParamBase {
  readonly type: "string";
  /**
   * For localStorage: store the value JSON-encoded (`"foo"`) rather than as a
   * plain string (`foo`). Arrays and ranges are always JSON in localStorage.
   */
  readonly jsonEncoded?: boolean;
  /**
   * Base value of a filter that is never "off" (a radio group): an absent, empty
   * or unrecognised value resolves to this instead of nothing, so the URL always
   * shows a real value (`torn=noTorn`, never `torn=`). Declared here so it lives
   * in exactly one place — the page must not carry its own default.
   */
  readonly defaultValue?: string;
  /**
   * Accepted values: anything else in the URL or in localStorage falls back to
   * `defaultValue` instead of being applied.
   */
  readonly allowedValues?: readonly string[];
}

export type FilterParam =
  ArrayFilterParam | RangeFilterParam | NumberFilterParam | StringFilterParam;

/** Any page's config. Configs are declared `as const`, hence `readonly`. */
export type FilterConfig = readonly FilterParam[];

/**
 * What ONE declared param decodes to. Its type gives the shape; whether it can
 * additionally be `undefined` is decided by the two escapes from a concrete
 * value — the same two the codec has:
 *  - a param with a `storageKey` always has a cache to fall back on, so it always
 *    resolves (`[]`, `{}`, or the stored value);
 *  - one without falls back to `undefined`, which the pages read as "unspecified,
 *    keep what you have" (the colour filter, analytics' data-derived selections);
 *  - a string declaring a `defaultValue` is never off, so it never resolves to
 *    nothing whether it is stored or not.
 * A number is never stored by any config, and `Number("x")` is not a number, so
 * it is always optional.
 */
export type FilterValueOf<E extends FilterParam> = E extends {
  type: "array";
}
  ? E extends { storageKey: string }
    ? string[]
    : string[] | undefined
  : E extends { type: "range" }
    ? E extends { storageKey: string }
      ? FilterRange
      : FilterRange | undefined
    : E extends { type: "number" }
      ? number | undefined
      : E extends { type: "string" }
        ? E extends { defaultValue: string }
          ? string
          : string | undefined
        : never;

/**
 * Every value a config decodes to, keyed by its own `paramKey`s — what
 * `hydrateFromUrl` hands back. This is why the configs are declared `as const`:
 * annotating one `FilterParam[]` erases the literal `paramKey`s and collapses
 * this back to an index signature.
 */
export type HydratedValues<C extends FilterConfig> = {
  [E in C[number] as E["paramKey"]]: FilterValueOf<E>;
};

/**
 * What ONE declared param may be mirrored back OUT as. Wider than what the same
 * param decodes to, on one axis only: an array is mirrored from whatever the
 * page's selection holds, and a selection carries the `key`s of the items the
 * filter offers — which the API sometimes numbers (incident ids). `encode`
 * stringifies each element, so the URL that comes out is identical either way;
 * accepting both here is what spares the pages a cast on the way out.
 */
export type MirrorValueOf<E extends FilterParam> = E extends { type: "array" }
  ? readonly (string | number)[] | undefined
  : FilterValueOf<E>;

/**
 * What `mirrorToUrl` accepts: the config's own keys, type-checked, plus any
 * others the caller happens to hold. A SUPERSET is deliberate — analytics passes
 * its whole filter set while `config` is the per-chart subset — and the keys
 * outside `config` are ignored, as the doc on `mirrorToUrl` says.
 */
export type MirrorValues<C extends FilterConfig> = Partial<{
  [E in C[number] as E["paramKey"]]: MirrorValueOf<E>;
}> &
  Record<string, unknown>;

/** Decoded filter values keyed by `paramKey`, when the config is not statically known. */
export type FilterValues = Record<string, unknown>;

/**
 * Config-driven sync between URL query params and the various filter backends.
 * **The URL is the source of truth and it is meant to be typed into**: every
 * filter a page declares is always written to the query string, empty ones
 * included (`?pkg=&trk=&days=3-10&…`), so the address bar is a complete, editable
 * form of what is displayed. Clearing a param's value switches that filter off;
 * typing a value switches it on — except for a param declaring a `defaultValue`
 * (a radio group, which always has a value): clearing it restores that default.
 *
 * **An absent key is not an empty one.** `days=` is the user switching that
 * filter off, and the URL is authoritative for it. A MISSING `days` says nothing
 * about the filter, and resolves to its last cached value instead — which is what
 * makes the filters survive a detour through a page that does not declare them
 * (`/map` → `/kpi` → `/map`: the KPI tab link can only carry the four params KPI
 * declares, so `trk`/`days`/`status`/… come back absent). localStorage is that
 * cache; `mirrorToUrl` then republishes the restored value, so the address bar is
 * complete again on the next tick.
 *
 * The two entry points:
 *  - `hydrateFromUrl` — on page load: every param declared in `config` is applied,
 *    present or not. A value overwrites localStorage (a hand-edited link wins), an
 *    empty one clears it, an absent one is restored from it. All resolved values are
 *    returned so the page can assign its own fields from them — it must NOT re-read
 *    localStorage itself.
 *  - `mirrorToUrl` — on load and after every filter change: the page passes its
 *    WHOLE filter state and the service writes it to both the URL (keys with no
 *    value included) and localStorage. It is the single writer of both, so a page
 *    handler never touches `localStorage` directly.
 *
 * Consequence: a navigation that should KEEP the current filters has to carry
 * them explicitly — `queryParamsHandling: "merge"` for same-page updates,
 * `navigateCarryingFilters` / `carriedParams` for cross-page hops (the latter is
 * what the dashboard tab links use).
 *
 * Critical rule: only params declared in `config` are ever touched. Unknown /
 * unmapped URL params are ignored, never stripped.
 */
@Injectable({ providedIn: "root" })
export class FilterUrlSyncService {
  private readonly router = inject(Router);

  /**
   * Apply the URL to every param the page declares: resolve it, persist the
   * localStorage-backed ones to their storage key (URL wins on load), and return
   * every resolved value keyed by paramKey.
   *
   * A declared param present with no value — the `pkg=` a user gets by deleting
   * the text after the `=` — decodes to the empty value for its type ([] for
   * arrays, {} for ranges, undefined otherwise) and CLEARS its backend. That is
   * what makes the URL authoritative: an empty value means that filter is off. A
   * param declaring a `defaultValue` has no "off" state: it decodes to that value
   * instead, so `mirrorToUrl` writes it straight back into the URL.
   *
   * A param the URL does not mention at all is a different case (see the class
   * doc): it resolves to `cachedValue` rather than to nothing. Params outside
   * `config` are left alone either way.
   *
   * The return type is derived from `config` itself, so a page reads its filters
   * off the result with no cast — provided the config is declared `as const`.
   */
  public hydrateFromUrl<C extends FilterConfig>(
    route: ActivatedRoute,
    config: C
  ): HydratedValues<C> {
    const params = route.snapshot.queryParamMap;
    const values: FilterValues = {};

    for (const entry of config) {
      // `get` returns null only when the key is missing entirely — a wiped value
      // comes back as "". The two are deliberately NOT the same thing here: one
      // means "off", the other "unspecified, use the cache".
      const raw = params.get(entry.paramKey);
      const value =
        raw === null ? this.cachedValue(entry) : this.decode(raw, entry);
      values[entry.paramKey] = value;
      // Idempotent for a restored value, and the write is what clears the key
      // for an explicitly empty one.
      this.writeStorage(entry, value);
    }

    // The one cast the seam still needs: the loop above builds the object key by
    // key, which no amount of typing makes the compiler follow. It stands in for
    // the ~32 casts the pages used to carry.
    return values as HydratedValues<C>;
  }

  /**
   * Write the page's whole filter state to the URL and to localStorage.
   *
   * **`values` must be complete**: every param in `config` is written, and one
   * missing from `values` is written empty — the service does not read the old
   * value back from anywhere. Each page therefore mirrors from a single
   * `mirrorFilters()` that lists all of its fields, rather than passing the one
   * filter it just changed.
   *
   * A param with no value is written empty (`pkg=`) rather than dropped: the key
   * has to stay in the query string for the user to be able to type into it, and
   * a page whose filters are all off still shows the full list. Params outside
   * `config` are preserved (merge).
   *
   * `clearKeys` is the opposite operation: those params are REMOVED from the query
   * string. Use it for keys the page has stopped declaring — analytics switching
   * to a chart that has no such filter — never for a filter that is merely off,
   * which stays visible as `key=`. They are outside `config`, so their
   * localStorage value (if any) is deliberately left untouched: dropping `pkg`
   * from an analytics URL must not turn the container filter off for kpi/map/list.
   */
  public mirrorToUrl<C extends FilterConfig>(
    route: ActivatedRoute,
    config: C,
    values: MirrorValues<C>,
    clearKeys: string[] = []
  ): void {
    const queryParams: Params = {};

    for (const entry of config) {
      const value = this.orDefault(values[entry.paramKey], entry);
      queryParams[entry.paramKey] = this.encode(value, entry.type);
      this.writeStorage(entry, value);
    }

    // null is the only value `merge` drops, so it is what removes a key; "" is
    // reserved for "declared filter, currently off". Applied after the config
    // loop, so a key in both is dropped.
    for (const key of clearKeys) {
      queryParams[key] = null;
    }

    this.router.navigate([], {
      relativeTo: route,
      queryParams,
      queryParamsHandling: "merge",
      replaceUrl: true
    });
  }

  /**
   * Navigate to another page, carrying over only the query params that the
   * TARGET page declares in `targetConfig`.
   *
   * Deliberately not `queryParamsHandling: "merge"`: merging would leak params
   * the target never reads (a map's `trk`/`rad`/`status` into `/incident/12`, say)
   * and they would then be mirrored back out of that page's URL. Passing the
   * intersection explicitly with the default (replace) handling keeps the shared
   * filters and drops the rest — which also means a `/map/tracker` position
   * (`pos`/`hrs`) is dropped for free, as no page config declares it.
   */
  public navigateCarryingFilters(
    route: ActivatedRoute,
    commands: unknown[],
    targetConfig: FilterConfig
  ): void {
    this.router.navigate(commands, {
      queryParams: this.carriedParams(route, targetConfig)
    });
  }

  /**
   * The subset of the current URL's query params that the target page declares —
   * the intersection `navigateCarryingFilters` navigates with, exposed for
   * callers that need it as a `[queryParams]` binding instead (the dashboard tab
   * links, which must carry the filters over because the URL is authoritative).
   *
   * Every `paramKey` is globally unique, so this intersection is all the filtering
   * a hop needs: a key the target does not declare is simply not carried.
   */
  public carriedParams(
    route: ActivatedRoute,
    targetConfig: FilterConfig
  ): Params {
    const current = route.snapshot.queryParamMap;
    const queryParams: Params = {};

    for (const entry of targetConfig) {
      if (current.has(entry.paramKey)) {
        queryParams[entry.paramKey] = current.get(entry.paramKey);
      }
    }

    return queryParams;
  }

  // ── codec ──────────────────────────────────────────────────────────────

  private decode(raw: string, entry: FilterParam): unknown {
    // An empty value is the "filter off" state: it is what the user leaves behind
    // after wiping a value in the address bar. (Also keeps Number("") from
    // decoding an empty `span=` as 0.)
    if (raw === "") {
      return this.emptyValue(entry);
    }
    switch (entry.type) {
      case "array":
        return raw.split(",").filter((v) => v.length > 0);
      case "range":
        return this.decodeRange(raw);
      case "number": {
        const n = Number(raw);
        return Number.isNaN(n) ? this.emptyValue(entry) : n;
      }
      case "string":
      default:
        return this.validate(raw, entry);
    }
  }

  /**
   * What a param holds when the URL (or localStorage) gives nothing usable: its
   * declared `defaultValue`, or the empty value of its type when it has none.
   */
  private emptyValue(entry: FilterParam): unknown {
    if (entry.type === "string" && entry.defaultValue !== undefined) {
      return entry.defaultValue;
    }
    if (entry.type === "array") {
      return [];
    }
    if (entry.type === "range") {
      return {};
    }
    return undefined;
  }

  /**
   * Self-heal a hand-typed or stale value: one outside the param's declared
   * `allowedValues` falls back to its default rather than being applied.
   */
  private validate(raw: string, entry: StringFilterParam): unknown {
    return entry.allowedValues && !entry.allowedValues.includes(raw)
      ? this.emptyValue(entry)
      : raw;
  }

  /**
   * A radio filter has no "off" state, so a missing or empty value on the way OUT
   * resolves to its declared default too: the URL and localStorage always show the
   * option that is actually applied, whatever the page happened to pass.
   */
  private orDefault(value: unknown, entry: FilterParam): unknown {
    if (entry.type !== "string" || entry.defaultValue === undefined) {
      return value;
    }
    return value === undefined || value === null || value === ""
      ? entry.defaultValue
      : value;
  }

  private encode(value: unknown, type: FilterType): string {
    if (value === null || value === undefined) {
      return "";
    }
    switch (type) {
      case "array":
        return Array.isArray(value)
          ? value.map((v) => String(v)).join(",")
          : "";
      case "range":
        return this.encodeRange(value as FilterRange);
      case "number":
      case "string":
      default:
        return String(value);
    }
  }

  private decodeRange(raw: string): FilterRange {
    const [start, end] = raw.split("-");
    const range: FilterRange = {};
    if (start !== undefined && start !== "") {
      const s = Number(start);
      if (!Number.isNaN(s)) range.start = s;
    }
    if (end !== undefined && end !== "") {
      const e = Number(end);
      if (!Number.isNaN(e)) range.end = e;
    }
    return range;
  }

  private encodeRange(range: FilterRange): string {
    if (!range || (range.start === undefined && range.end === undefined)) {
      return "";
    }
    const start = range.start === undefined ? "" : String(range.start);
    const end = range.end === undefined ? "" : String(range.end);
    return `${start}-${end}`;
  }

  // ── localStorage ─────────────────────────────────────────────────────────

  /**
   * What a param the URL says nothing about resolves to: its last cached value,
   * so a hop through a page that does not declare it — the `/kpi` tab link, which
   * can only carry KPI's own four params — does not switch it off.
   *
   * Deliberately NOT `emptyValue`: an absent param is not an off one. A param the
   * service does not store has no cache, so it falls back to its declared default
   * when it has one (a radio group always has a value) and otherwise to
   * `undefined`, which the pages read as "unspecified, keep what you have" — the
   * colour filter keeps the selection `readColorStatusSelection()` restored, and
   * analytics keeps the defaults `initFilters` set.
   */
  private cachedValue(entry: FilterParam): unknown {
    if (entry.storageKey) {
      return this.readStorage(entry);
    }
    return entry.type === "string" ? entry.defaultValue : undefined;
  }

  /**
   * Decode a filter's cached value from its `storageKey`, the inverse of
   * `writeStorage`. A missing key means the filter was off when it was last
   * written, so it resolves to the param's empty value — as does a corrupt or
   * wrong-shaped entry, since localStorage is editable by hand and survives
   * across versions of the app.
   */
  private readStorage(entry: FilterParam): unknown {
    const raw = localStorage.getItem(entry.storageKey);
    if (raw === null) {
      return this.emptyValue(entry);
    }
    switch (entry.type) {
      case "array": {
        const parsed = this.parseJson(raw);
        return Array.isArray(parsed)
          ? parsed.map((value) => String(value))
          : this.emptyValue(entry);
      }
      case "range": {
        const parsed = this.parseJson(raw);
        if (typeof parsed !== "object" || parsed === null) {
          return this.emptyValue(entry);
        }
        const { start, end } = parsed as FilterRange;
        const range: FilterRange = {};
        if (typeof start === "number" && !Number.isNaN(start)) {
          range.start = start;
        }
        if (typeof end === "number" && !Number.isNaN(end)) {
          range.end = end;
        }
        return range;
      }
      case "number": {
        const n = Number(raw);
        return Number.isNaN(n) ? this.emptyValue(entry) : n;
      }
      case "string":
      default: {
        const value = entry.jsonEncoded ? this.parseJson(raw) : raw;
        return typeof value === "string"
          ? this.validate(value, entry)
          : this.emptyValue(entry);
      }
    }
  }

  /** `JSON.parse` that yields null instead of throwing on a corrupt entry. */
  private parseJson(raw: string): unknown {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * Cache a filter's value under its `storageKey`, removing the key when the
   * filter is off. A param with no `storageKey` lives on the page and is skipped,
   * which is why both entry points can call this unconditionally.
   */
  private writeStorage(entry: FilterParam, decoded: unknown): void {
    const key = entry.storageKey;
    if (!key) {
      return;
    }
    switch (entry.type) {
      case "array": {
        const arr = Array.isArray(decoded) ? decoded : [];
        if (arr.length === 0) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify(arr));
        }
        break;
      }
      case "range": {
        const range = (decoded as FilterRange) ?? {};
        if (range.start === undefined && range.end === undefined) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify(range));
        }
        break;
      }
      case "number": {
        if (decoded === undefined || decoded === null) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, String(decoded));
        }
        break;
      }
      case "string":
      default: {
        if (decoded === undefined || decoded === null || decoded === "") {
          localStorage.removeItem(key);
        } else if (entry.jsonEncoded) {
          localStorage.setItem(key, JSON.stringify(decoded));
        } else {
          localStorage.setItem(key, String(decoded));
        }
        break;
      }
    }
  }
}
